import { VideoEditor } from '../VideoEditor';
import { TrackManager } from '../lib/TrackManager';
import { TimelineMode } from './TimelineMode';
import { Element } from '../models/Element';
import { Track } from '../models/Track';
import {
  AbstractElementCommand,
  AffectedElementOptions,
  RippleOptions,
} from './AbstractCommand';

/**
 * Command to move an element to a new position on the timeline
 * Uses direct object references to elements and tracks
 */
export class ShiftElementCommand extends AbstractElementCommand {
  private newStartTime: number;
  private originalState: Record<string, any>;
  private trackManager: TrackManager;
  private targetTrack?: Track;

  /**
   * Creates a new ShiftElementCommand
   * @param editor The video editor instance
   * @param element The element to move
   * @param newStartTime The new start time for the element (in seconds)
   * @param targetTrack Optional target track if moving to a different track
   */
  constructor(
    editor: VideoEditor,
    element: Element,
    newStartTime: number,
    targetTrack?: Track,
  ) {
    super(editor, element);

    // Get TrackManager from the editor instead of creating a new one
    this.trackManager = this.editor.getTrackManager();

    // Ensure time is never negative
    this.newStartTime = Math.max(0, this.snapTimeToFrame(newStartTime));

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);

    // Store affected elements for undo
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    // Use the trackManager instance with direct object references
    const result = this.trackManager.shiftElement(
      this.sourceElement,
      this.newStartTime,
      this.getTimelineMode(), // Pass the mode explicitly
    );

    if (result) {
      // Recalculate timeline duration after successful move
      this.recalculateTimeline();
    }

    return result;
  }

  undo(): boolean {
    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    return `Move element ${this.sourceElement.id} to ${this.newStartTime}s`;
  }
}
